import { useEffect, useState } from "react";
import { ArrowLeft, Users, Store, Package, Inbox, ShieldCheck, Receipt, SlidersHorizontal, Plug, DatabaseZap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Loader } from "@/components/portal/ui";
import { StatusBadge } from "@/components/portal/StatusBadge";
import { SyncBadge } from "@/components/portal/ProvidersAdmin";
import { ConnectorPanel } from "@/components/control/ConnectorPanel";
import { MigrationPanel } from "@/components/control/MigrationPanel";

const MODULE_LABELS = {
  packages: { de: "Pakete / Abos", fr: "Forfaits / abos", it: "Pacchetti / abbonamenti", en: "Packages / subscriptions" },
  approvals: { de: "Freigaben", fr: "Approbations", it: "Approvazioni", en: "Approvals" },
  ads: { de: "Werbung / Promotions", fr: "Publicité / promotions", it: "Pubblicità / promozioni", en: "Ads / promotions" },
  payments: { de: "Zahlungen / Rechnungen", fr: "Paiements / factures", it: "Pagamenti / fatture", en: "Payments / invoices" },
  reports: { de: "Reports", fr: "Rapports", it: "Report", en: "Reports" },
  documents: { de: "Dokumente", fr: "Documents", it: "Documenti", en: "Documents" },
  jobs: { de: "Stellen / Jobs", fr: "Postes / emplois", it: "Posizioni / lavoro", en: "Jobs" },
};
const FEATURE_LABELS = {
  self_registration: { de: "Selbst-Registrierung", fr: "Auto-inscription", it: "Auto-registrazione", en: "Self-registration" },
  google_auth: { de: "Google-Login", fr: "Connexion Google", it: "Login Google", en: "Google login" },
};

function Toggle({ on, onClick, testid, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} aria-pressed={on}
      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
      style={{ background: on ? "var(--accent)" : "var(--surface-alt)" }}>
      <span className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
        style={{ transform: on ? "translateX(24px)" : "translateX(4px)" }} />
    </button>
  );
}

function Empty({ t }) {
  return <Card className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.none")}</Card>;
}

export function PortalDetail({ portalKey, onBack }) {
  const { t, L } = useI18n();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("providers");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/control/portal/${portalKey}`).then(({ data }) => setData(data));
  }, [portalKey]);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data: res } = await api.patch(`/control/portal/${portalKey}/config`, payload);
      setData((d) => ({ ...d, config: { ...d.config, active_modules: res.active_modules, features: res.features } }));
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  const toggleModule = (m) => {
    const cur = data.config.active_modules || [];
    save({ active_modules: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m] });
  };
  const toggleFeature = (f) => {
    const feats = { ...(data.config.features || {}) };
    feats[f] = !feats[f];
    save({ features: feats });
  };

  if (!data) return <Loader />;
  const c = data.counts;
  const tabs = [
    { key: "providers", label: t("control.tabProviders"), icon: Store, n: c.providers },
    { key: "users", label: t("control.tabUsers"), icon: Users, n: c.members },
    { key: "packages", label: t("control.tabPackages"), icon: Package, n: data.packages.length },
    { key: "settings", label: L({ de: "Module & Features", fr: "Modules & fonctions", it: "Moduli & funzioni", en: "Modules & features" }), icon: SlidersHorizontal },
    { key: "migration", label: L({ de: "Live-Migration", fr: "Migration live", it: "Migrazione live", en: "Live migration" }), icon: DatabaseZap },
    { key: "connector", label: "WordPress", icon: Plug },
  ];
  const stats = [
    { label: t("control.tabProviders"), value: c.providers, icon: Store },
    { label: t("control.pending"), value: c.pending, icon: ShieldCheck },
    { label: t("control.customers"), value: c.customers, icon: Users },
    { label: t("stat.open_inquiries"), value: c.open_inquiries, icon: Inbox },
    { label: t("control.subscribers"), value: c.active_subscriptions, icon: Package },
    { label: t("control.revenue"), value: c.revenue_paid, icon: Receipt },
    { label: t("control.openInvoices"), value: c.open_invoices, icon: Receipt },
  ];

  return (
    <div className="kw-fade" data-testid="portal-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm mb-4" style={{ color: "var(--text-muted)" }} data-testid="portal-detail-back">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>
      <h1 className="font-heading text-3xl font-bold" style={{ color: "var(--text)" }}>{data.portal.name}</h1>
      <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>{portalKey} · {data.portal.domain}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        {stats.map((s, i) => {
          const I = s.icon;
          return (
            <Card key={i} className="p-4">
              <I className="h-4 w-4 mb-2" style={{ color: "var(--accent)" }} />
              <div className="font-heading text-2xl font-bold" style={{ color: "var(--text)" }}>{s.value}</div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{s.label}</div>
            </Card>
          );
        })}
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto">
        {tabs.map((tb) => {
          const on = tab === tb.key;
          const I = tb.icon;
          return (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`portal-tab-${tb.key}`}
              className="inline-flex items-center gap-2 px-4 h-10 text-sm font-medium whitespace-nowrap transition-all"
              style={{ borderRadius: "var(--radius)", background: on ? "var(--primary)" : "var(--surface-alt)", color: on ? "var(--primary-fg)" : "var(--text)" }}>
              <I className="h-4 w-4" /> {tb.label} {tb.n != null && <span className="text-xs opacity-70">{tb.n}</span>}
            </button>
          );
        })}
      </div>

      {tab === "providers" && (
        <div className="space-y-2" data-testid="portal-providers">
          {data.providers.length === 0 && <Empty t={t} />}
          {data.providers.map((p) => (
            <Card key={p.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`portal-provider-${p.id}`}>
              <div className="h-10 w-10 shrink-0 overflow-hidden" style={{ background: "var(--surface-alt)", borderRadius: "var(--radius)" }}>
                {p.cover_image && <img src={p.cover_image} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate" style={{ color: "var(--text)" }}>{p.business_name}</div>
                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{L(p.location_name) || "—"}</div>
              </div>
              <SyncBadge status={p.sync_status} />
              {p.is_demo && (
                <span className="text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap" title="Synthetische Demo-Daten – nicht echt" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>
                  Demo Anbieter – nicht echt
                </span>
              )}
              <StatusBadge status={p.status} />
            </Card>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-2" data-testid="portal-users">
          {data.users.length === 0 && <Empty t={t} />}
          {data.users.map((u, i) => (
            <Card key={u.id + i} className="p-3 flex flex-wrap items-center gap-3" data-testid={`portal-user-${u.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate" style={{ color: "var(--text)" }}>{u.name}</div>
                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{u.email}</div>
              </div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--accent)" }}>{t(`roles.${u.role}`)}</span>
              <span className="text-xs px-2 py-1 rounded-full" style={{ background: u.status === "active" ? "#DCFCE7" : "#FEE2E2", color: u.status === "active" ? "#15803D" : "#B91C1C" }}>{u.status}</span>
            </Card>
          ))}
        </div>
      )}

      {tab === "packages" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="portal-packages">
          {data.packages.length === 0 && <Empty t={t} />}
          {data.packages.map((p) => (
            <Card key={p.id} className="p-4" data-testid={`portal-package-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading font-bold" style={{ color: "var(--text)" }}>{L(p.name)}</span>
                {!p.active && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>{t("promo.inactive")}</span>}
              </div>
              <div className="mt-1 text-2xl font-heading font-bold" style={{ color: "var(--text)" }}>{p.price === 0 ? t("pkg.free") : `${p.currency} ${p.price}`}</div>
              <div className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>{p.subscribers} · {t("control.subscribers")}</div>
            </Card>
          ))}
        </div>
      )}

      {tab === "settings" && (
        <div className="space-y-5" data-testid="portal-settings">
          <Card className="p-5">
            <div className="text-sm font-semibold mb-1" style={{ color: "var(--text)" }}>{L({ de: "Module", fr: "Modules", it: "Moduli", en: "Modules" })}</div>
            <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>{L({ de: "Funktionen für dieses Portal ein-/ausschalten. Wirkt sofort im Cockpit.", fr: "Activer/désactiver les fonctions de ce portail. Effet immédiat.", it: "Attiva/disattiva le funzioni di questo portale. Effetto immediato.", en: "Enable/disable features for this portal. Takes effect immediately." })}</p>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {(data.config.toggleable_modules || []).map((m) => (
                <div key={m} className="flex items-center justify-between py-3">
                  <span className="text-sm" style={{ color: "var(--text)" }}>{MODULE_LABELS[m] ? L(MODULE_LABELS[m]) : m}</span>
                  <Toggle testid={`toggle-module-${m}`} on={(data.config.active_modules || []).includes(m)} disabled={saving} onClick={() => toggleModule(m)} />
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-sm font-semibold mb-1" style={{ color: "var(--text)" }}>{L({ de: "Auth-Features", fr: "Fonctions d'auth", it: "Funzioni auth", en: "Auth features" })}</div>
            <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>{L({ de: "Login- & Registrierungs-Optionen für dieses Portal.", fr: "Options de connexion & inscription.", it: "Opzioni di accesso e registrazione.", en: "Login & registration options for this portal." })}</p>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {(data.config.toggleable_features || []).map((f) => (
                <div key={f} className="flex items-center justify-between py-3">
                  <span className="text-sm" style={{ color: "var(--text)" }}>{FEATURE_LABELS[f] ? L(FEATURE_LABELS[f]) : f}</span>
                  <Toggle testid={`toggle-feature-${f}`} on={!!(data.config.features || {})[f]} disabled={saving} onClick={() => toggleFeature(f)} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
      {tab === "migration" && <MigrationPanel portalKey={portalKey} />}
      {tab === "connector" && <ConnectorPanel portalKey={portalKey} />}
    </div>
  );
}
